import type { NavItemConfig } from "@/types/nav";

/**
 * APAL Portal Navigation Configuration
 *
 * Navigation items for the APAL dashboard sidebar.
 * Disabled items indicate "Coming Soon" features.
 *
 * minTier controls sidebar visibility:
 * - Tier 1: standard, employee, buyer (all users)
 * - Tier 2: team_lead, vendor, broker
 * - Tier 3: manager, seller
 * - Tier 4: executive
 * - Tier 5: admin
 *
 * Items without minTier default to visible for all authenticated users.
 */

/**
 * Extended nav item type with tier-based access control.
 * The minTier field controls visibility in the sidebar.
 */
export interface ApalNavItemConfig extends NavItemConfig {
	/** Minimum tier level required to see this nav item (1-5). Omit for all users. */
	minTier?: number;
	/** Nested items with tier support */
	items?: ApalNavItemConfig[];
}

export const apalNavItems: ApalNavItemConfig[] = [
	{
		key: "dashboard",
		title: "",
		items: [
			{
				key: "overview",
				title: "Dashboard",
				href: "/dashboard",
				icon: "house",
				matcher: { type: "equals", href: "/dashboard" }
			},
		],
	},
	{
		key: "management",
		title: "Management",
		items: [
			{
				key: "goals",
				title: "Goals",
				href: "/dashboard/goals",
				icon: "target",
				matcher: { type: "startsWith", href: "/dashboard/goals" }
			},
			{
				key: "employees",
				title: "Employees",
				href: "/dashboard/employees",
				icon: "users",
				minTier: 3,
				matcher: { type: "startsWith", href: "/dashboard/employees" },
				items: [
					{
						key: "employees-import",
						title: "Import",
						href: "/dashboard/employees/import",
						matcher: { type: "startsWith", href: "/dashboard/employees/import" }
					},
					{
						key: "employees-import-history",
						title: "Import History",
						href: "/dashboard/employees/import/history",
						matcher: { type: "equals", href: "/dashboard/employees/import/history" }
					},
				],
			},
			{
				key: "team",
				title: "Team",
				href: "/dashboard/team",
				icon: "users",
				minTier: 2,
				matcher: { type: "startsWith", href: "/dashboard/team" }
			},
			{
				key: "company",
				title: "Company",
				href: "/dashboard/company",
				icon: "building",
				minTier: 4,
				matcher: { type: "startsWith", href: "/dashboard/company" }
			},
		],
	},
	{
		key: "analytics",
		title: "Analytics",
		items: [
			{
				key: "analytics",
				title: "Analytics",
				href: "/dashboard/analytics",
				icon: "chart-pie",
				minTier: 3,
				matcher: { type: "startsWith", href: "/dashboard/analytics" }
			},
			{
				key: "behavioral",
				title: "Behavioral Scores",
				href: "/dashboard/behavioral",
				icon: "chart-pie",
				minTier: 3,
				matcher: { type: "startsWith", href: "/dashboard/behavioral" }
			},
		],
	},
	{
		key: "support",
		title: "Support",
		items: [
			{
				key: "help-center",
				title: "Help Center",
				href: "/dashboard/help",
				icon: "graduation-cap",
				matcher: { type: "startsWith", href: "/dashboard/help" },
				"data-testid": "nav-help-center",
			},
		],
	},
];
